import React, { useState, useEffect } from "react";
import { View, StyleSheet, Platform, type ViewStyle } from "react-native";
import DraggableFlatList, { ScaleDecorator } from "react-native-draggable-flatlist";
import ActivityCard from "../ActivityCard/ActivityCard";
import { sortResourceItemsOfModules } from "../../../utils/sortResourceItemsOfModules";

type ActivityCardListItem = {
  id: string;
  title: string;
  type: string;
  name?: string;
  isPendingCreation?: boolean;
  processingLabel?: string;
  [key: string]: unknown;
};

type ActivityCardListModule = {
  id: string;
  aiTutors?: ActivityCardListItem[];
  pages?: ActivityCardListItem[];
  axioResources?: ActivityCardListItem[];
  assignments?: ActivityCardListItem[];
  reviews?: ActivityCardListItem[];
  projects?: ActivityCardListItem[];
  assessment?: (ActivityCardListItem & { moduleHasAssessment?: { title?: string } | null }) | null;
  resourceOrder?: string[];
};

const ActivityCardList = ({
  module,
  onItemOptionsPress,
  style,
  onDragEnd,
  onResetILE,
  resettingILEId,
  pendingAssessmentCreation,
}) => {
  const typedModule = module as ActivityCardListModule;
  const [localItems, setLocalItems] = useState<ActivityCardListItem[]>([]);

  useEffect(() => {
    const hasPendingAssessmentCreation = Boolean(pendingAssessmentCreation?.jobId);
    const assessmentItems = typedModule?.assessment?.id
      ? [
          {
            ...typedModule.assessment,
            title: `Assessment for ${typedModule.assessment.moduleHasAssessment?.title || "Unknown Module"}`,
            type: "Assessment",
          },
        ]
      : hasPendingAssessmentCreation
        ? [
            {
              id: `pending-assessment-${typedModule?.id}`,
              title: "Assessment is being created...",
              type: "Assessment",
              isPendingCreation: true,
              processingLabel: pendingAssessmentCreation?.status || "queued",
            },
          ]
        : [];

    const items = [
      ...(typedModule?.aiTutors || []).map((item: ActivityCardListItem) => ({
        ...item,
        type: "Interactive Learning Experience",
      })),
      ...(typedModule?.pages || []).map((item: ActivityCardListItem) => ({
        ...item,
        type: "Page",
      })),
      ...(typedModule?.axioResources || []).map((item: ActivityCardListItem) => ({
        ...item,
        title: item.name, // Map name to title for consistency
        type: "Resource",
      })),
      ...(typedModule?.assignments || []).map((item: ActivityCardListItem) => ({
        ...item,
        title: item.name,
        type: "Assignment",
      })),
      ...(typedModule?.reviews || []).map((item: ActivityCardListItem) => ({
        ...item,
        title: `Review for ${item.hasReviewFor?.moduleHasAssessment?.title || "Unknown Module"}'s Assessment`,
        type: "Review",
      })),
      ...assessmentItems,
      ...(typedModule?.projects || []).map((item: ActivityCardListItem) => ({
        ...item,
        title: item.name,
        type: "Project",
      })),
    ];

    const sortedItems = sortResourceItemsOfModules(items, typedModule?.resourceOrder) as ActivityCardListItem[];

    setLocalItems(sortedItems);
  }, [typedModule, pendingAssessmentCreation]);

  const handleDragEnd = (data: ActivityCardListItem[]) => {
    setLocalItems(data);
    if (onDragEnd) {
      onDragEnd(data.filter((item: ActivityCardListItem) => !item.isPendingCreation));
    }
  };

  const renderItem = ({ item, drag, isActive }) => {
    return (
      <ScaleDecorator>
        <ActivityCard
          key={item.id}
          item={item}
          title={item.title}
          type={item.type}
          onOptionsPress={(action) => {
            if (!item.isPendingCreation) {
              onItemOptionsPress(action, item);
            }
          }}
          onLongPress={item.isPendingCreation ? () => {} : drag}
          style={[styles.card, isActive && styles.draggingItem]}
          onResetILE={onResetILE}
          resettingILEId={resettingILEId}
        />
      </ScaleDecorator>
    );
  };

  return (
    <View style={[styles.container, style]}>
      <DraggableFlatList
        data={localItems}
        onDragEnd={({ data }) => handleDragEnd(data)}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        dragItemOverflow={true}
        activationDistance={5}
        autoscrollSpeed={50}
        containerStyle={{ flex: 1 }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  card: {
    width: "100%",
    marginBottom: 8,
  },
  draggingItem: {
    opacity: 0.9,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 1,
    ...(Platform.OS === "web" && ({ cursor: "grabbing" } as unknown as ViewStyle)),
  },
});

export default ActivityCardList;
